import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import AccountShell from "../../components/account/AccountShell";
import { useSession } from "../../hooks/useSession";
import { apiFetchShopper } from "../../lib/api";

export default function ShopperAccountPage() {
  const { shopper, logoutShopper, refreshSession } = useSession();

  const [email, setEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    setEmail(shopper?.email || "");
  }, [shopper]);

  async function handleEmailUpdate(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setEmailLoading(true);

    try {
      await apiFetchShopper("/shopper-auth/me", {
        method: "PATCH",
        body: JSON.stringify({ email: email.trim() }),
      });

      await refreshSession();
      setSuccess("Email updated.");
    } catch (err) {
      setError(err?.message || "Could not update email.");
    } finally {
      setEmailLoading(false);
    }
  }

  async function handlePasswordChange(e) {
    e.preventDefault();
    setError("");
    setSuccess("");
    setPasswordLoading(true);

    try {
      await apiFetchShopper("/shopper-auth/change-password", {
        method: "POST",
        body: JSON.stringify({
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      setCurrentPassword("");
      setNewPassword("");
      setSuccess("Password updated.");
    } catch (err) {
      setError(err?.message || "Could not change password.");
    } finally {
      setPasswordLoading(false);
    }
  }

  return (
    <AccountShell
      title="My Account"
      navItems={[
        { label: "Account", to: "/account" },
        { label: "Edit mannequin", to: "/account/avatar" },
      ]}
    >
      <style>{`
        .merchant-settings-form {
          display: flex;
          flex-direction: column;
          gap: 14px;
          margin-top: 6px;
        }

        .merchant-settings-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .merchant-settings-label {
          font-size: 13px;
          font-weight: 600;
          color: rgba(40, 40, 40, 0.78);
        }

        .merchant-settings-input,
        .merchant-settings-readonly {
          width: 100%;
          border-radius: 14px;
          border: 1px solid rgba(40, 40, 40, 0.08);
          background: rgba(255, 255, 255, 0.82);
          padding: 12px 14px;
          font-size: 14px;
          color: rgba(20, 20, 20, 0.92);
          outline: none;
          box-sizing: border-box;
        }

        .merchant-settings-input:focus {
          border-color: rgba(106, 30, 45, 0.26);
          box-shadow: 0 0 0 3px rgba(106, 30, 45, 0.08);
        }

        .merchant-settings-actions {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          margin-top: 4px;
        }

        .merchant-settings-divider {
          height: 1px;
          background: rgba(40, 40, 40, 0.08);
          margin: 10px 0 2px;
        }

        .merchant-settings-error {
          font-size: 13px;
          color: #b4232c;
        }

        .merchant-settings-success {
          font-size: 13px;
          color: #2f6f45;
        }

        .merchant-settings-note {
          font-size: 13px;
          color: rgba(40, 40, 40, 0.62);
          line-height: 1.5;
        }

        .merchant-settings-logout {
          font-size: 13px;
          cursor: pointer;
          color: rgba(40,40,40,0.72);
          transition: color 0.15s ease;
          user-select: none;
        }

        .merchant-settings-logout:hover {
          color: #6a1e2d;
        }
      `}</style>

      <div className="public-card">
        <div className="public-card-title">Account</div>
        <div className="public-card-sub">
          Update your shopper login details and mannequin.
        </div>

        <form className="merchant-settings-form" onSubmit={handleEmailUpdate}>
          <div className="merchant-settings-field">
            <label className="merchant-settings-label">Email</label>
            <input
              className="merchant-settings-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="hello@youremail.com"
            />
          </div>

          <div className="merchant-settings-actions">
            <button
              className="gold-btn"
              type="submit"
              disabled={emailLoading}
            >
              {emailLoading ? "Updating..." : "Update email"}
            </button>
          </div>
        </form>

        <div className="merchant-settings-divider" />

        <form className="merchant-settings-form" onSubmit={handlePasswordChange}>
          <div className="merchant-settings-field">
            <label className="merchant-settings-label">Current password</label>
            <input
              className="merchant-settings-input"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Enter your current password"
            />
          </div>

          <div className="merchant-settings-field">
            <label className="merchant-settings-label">New password</label>
            <input
              className="merchant-settings-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Create a new password"
            />
          </div>

          <div className="merchant-settings-actions">
            <button
              className="gold-btn"
              type="submit"
              disabled={passwordLoading}
            >
              {passwordLoading ? "Updating..." : "Change password"}
            </button>
          </div>
        </form>

        <div className="merchant-settings-divider" />

        <div className="merchant-settings-form">
          <div>
            <div className="public-card-title" style={{ fontSize: 22 }}>
              Mannequin
            </div>
            <div className="merchant-settings-note">
              Update your mannequin’s shape, skin tone, hair, and sizing.
            </div>
          </div>

          <div className="merchant-settings-actions">
            <Link
              to="/account/avatar"
              className="gold-btn"
              style={{ textDecoration: "none" }}
            >
              Edit mannequin
            </Link>
          </div>
        </div>

        {error ? (
          <div className="merchant-settings-error" style={{ marginTop: 14 }}>
            {error}
          </div>
        ) : null}

        {success ? (
          <div className="merchant-settings-success" style={{ marginTop: 14 }}>
            {success}
          </div>
        ) : null}

        <div className="merchant-settings-actions" style={{ marginTop: 18 }}>
          <span
            className="merchant-settings-logout"
            onClick={logoutShopper}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                logoutShopper();
              }
            }}
          >
            Log out
          </span>
        </div>
      </div>
    </AccountShell>
  );
}